// Shared changelog data - source of truth for both website and service frontend

export interface ChangelogEntry {
  id: string;
  type: "book_update" | "general_news" | "feature" | "bug_fix";
  title: string;
  description: string;
  date: string; // ISO date string
  /**
   * Book revision this entry shipped in, calendar versioned as "YYYY.MM".
   * Set it on entries that changed the book itself, so a reader holding a copy
   * stamped "Revision 2026.08" can see what is new since theirs. Leave it unset
   * for platform news that did not change the book (website features, pricing,
   * announcements). Revisions before 2026.08 predate this scheme and are unset.
   * The book states its own revision in dibia/_quarto.yml - keep them in step.
   */
  bookRevision?: string;
  requiresSignIn?: boolean;
  isNew?: boolean;
  /**
   * Where a reader goes to actually use what this entry announced. Rendered as
   * the primary action in the announcement modal. Omit for entries with nowhere
   * specific to send someone - the modal then offers only the changelog link.
   */
  ctaUrl?: string;
  ctaLabelKey?: string;
}

/**
 * The book revision currently shipping, derived from the newest entry that
 * carries one. Derived rather than declared so there is no second constant to
 * drift out of step with the entries or with the book's own index.qmd.
 * Returns undefined before any revision-stamped entry exists.
 */
/**
 * Render a "YYYY.MM" revision as a month a reader can read at a glance -
 * "August 2026", localized. The numeric form is an identifier printed in the
 * book, useful for matching a copy you hold; it is not something to put in
 * front of someone deciding whether to buy. Use this for marketing surfaces
 * and keep the raw revision where it acts as an identifier.
 * Returns undefined for a malformed or missing revision rather than guessing.
 */
export function formatBookRevision(
  revision: string | undefined,
  locale = "en"
): string | undefined {
  if (!revision) return undefined;
  const match = /^(\d{4})\.(\d{2})$/.exec(revision);
  if (!match) return undefined;
  const [, year, month] = match;
  const monthIndex = Number(month) - 1;
  if (monthIndex < 0 || monthIndex > 11) return undefined;
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(new Date(Number(year), monthIndex, 1));
}

export function getCurrentBookRevision(): string | undefined {
  return changelogEntries
    .filter((entry) => entry.bookRevision)
    .sort((a, b) => b.date.localeCompare(a.date))[0]?.bookRevision;
}

export const changelogEntries: ChangelogEntry[] = [
  {
    id: "14c",
    type: "book_update",
    title: "Chapter 12 Updated for the July 2026 MCP Spec",
    description:
      "Chapter 12 now describes MCP 2026-07-28, which is wire-incompatible with 2025-era MCP in both directions. The initialize handshake and the session header are gone, requests are stateless, and capabilities that once lived in the core now arrive as negotiated extensions.",
    date: "2026-08-25",
    bookRevision: "2026.08",
    requiresSignIn: false,
    isNew: true,
  },
  {
    id: "15",
    type: "feature",
    title: "Course Slides for Every Chapter",
    description:
      "Every chapter of the book now has a slide deck you can download as a PDF. Chapters 1 and 2 are free for everyone, and Professional edition readers get the full set. When the book is revised, the slides are revised with it.",
    date: "2026-08-12",
    requiresSignIn: false,
    isNew: false,
    ctaUrl: "https://buy.multiagentbook.com/course",
    ctaLabelKey: "announcement.viewSlides",
  },
  {
    id: "14",
    type: "book_update",
    title: "Russian Edition, Localized Covers, and Completed Prefaces",
    description:
      "The book is now available in Russian, bringing the total to seven languages. Every translated edition also gets its own localized cover, and the Spanish, Japanese, and Chinese prefaces are now fully translated.",
    date: "2026-08-12",
    bookRevision: "2026.08",
    requiresSignIn: false,
    isNew: false,
  },
  {
    id: "13",
    type: "book_update",
    title: "Translated EPUB Editions Now Available",
    description:
      "All 5 book translations (French, German, Spanish, Japanese, Chinese) are now also available as Kindle-compatible EPUBs, alongside the existing PDFs. Translation PDFs were also updated with a Chapter 15 layout fix. Download from your dashboard.",
    date: "2026-08-10",
    bookRevision: "2026.08",
    requiresSignIn: false,
    isNew: false,
  },
  {
    id: "12",
    type: "book_update",
    title: "Book Translations Now Available in 5 Languages",
    description:
      "The full book is now available as translated PDFs in French, German, Spanish, Japanese, and Chinese. All translations are kept in sync with the English source. Download from your dashboard.",
    date: "2026-03-26",
    requiresSignIn: false,
    isNew: false,
  },
  {
    id: "11",
    type: "book_update",
    title: "Updated: Context Engineering & SWE Agent Chapters",
    description:
      "Chapter 4 updated with correct middleware signatures (async generators), loop-level hooks for preventing early stopping, and HeadTailCompaction as a first-class agent parameter. Chapter 15 rewritten with a code review agent example, benchmark data comparing compaction strategies, and step-by-step execution traces. Three new glossary entries added.",
    date: "2026-03-26",
    requiresSignIn: true,
    isNew: false,
  },
  {
    id: "10",
    type: "feature",
    title: "Website Now Available in 6 Languages",
    description:
      "The website and customer dashboard are now fully translated into French, German, Spanish, Chinese, and Japanese. Use the language switcher in the header to change languages. Note: Book content remains in English only for now.",
    date: "2026-01-04",
    requiresSignIn: false,
    isNew: false,
  },
  {
    id: "9",
    type: "feature",
    title: "Now Available on Amazon (Print Edition)",
    description:
      "The book is now available on Amazon as a print paperback! Purchase your physical copy at amazon.com/dp/B0G2BCQQJY. Note: Digital formats (PDF, EPUB) with lifetime updates and premium samples are exclusively available through this platform.",
    date: "2025-11-14",
    requiresSignIn: false,
    isNew: false,
  },
  {
    id: "8",
    type: "feature",
    title: "First Premium Code Sample Available",
    description:
      "Released the first end-to-end premium sample application - a complete multi-agent research assistant demonstrating practical implementation patterns. More samples covering additional frameworks and use cases will be added going forward.",
    date: "2025-11-10",
    requiresSignIn: false,
    isNew: false,
  },
  {
    id: "7",
    type: "book_update",
    title: "New Chapter: Protocols for Distributed Agents",
    description:
      "Learn about distributed agent architectures where components run across separate execution contexts - different machines, regions, or organizations. Explore Model Context Protocol (MCP) for standardized tool and context integration with agentic capabilities, and Agent-to-Agent Protocol (A2A) for cross-organizational collaboration.",
    date: "2025-11-04",
    requiresSignIn: true,
    isNew: false,
  },
  {
    id: "6",
    type: "book_update",
    title: "New Chapter: Ethics and Responsible AI for Multi-Agent Systems",
    description:
      "Learn why traditional AI ethics frameworks break down when agents can act autonomously: agentic noise and platform imbalance, emergent risks from agent interactions, distributed responsibility gaps, and security paradigm shifts. Includes practical middleware defenses and deployment checklists.",
    date: "2025-10-31",
    requiresSignIn: true,
    isNew: false,
  },
  {
    id: "5",
    type: "book_update",
    title: "New Chapter: Software Engineering Agent",
    description:
      "Added Chapter 15 teaching the foundational pattern used by modern AI coding assistants like GitHub Copilot, Cursor, and Claude Code: agent capability emerges from tools + prompts + memory. Learn how to build a software engineering agent that can autonomously write code, run tests, and iteratively improve solutions.",
    date: "2025-10-28",
    requiresSignIn: true,
    isNew: false,
  },
  {
    id: "4",
    type: "book_update",
    title: "New Chapter: Building Modern Web Experiences for Agent Applications",
    description:
      "Added Chapter 8 covering how to build production-ready web interfaces for multi-agent systems. Learn how to implement streaming event visualization, understand backend-frontend architecture patterns, and choose between WebSockets and SSE for real-time communication.",
    date: "2025-10-19",
    requiresSignIn: true,
    isNew: false,
  },
  {
    id: "3",
    type: "book_update",
    title: "New Chapter: Building Computer Use Agents",
    description:
      "Added a new chapter - Building Computer Use Agents (from scratch). If you have used tools like Manus, OpenAI operator, etc - agents that drive user interfaces such as web or desktop applications, this chapter shows how to build them!",
    date: "2025-09-27",
    requiresSignIn: true,
    isNew: false,
  },
  {
    id: "1",
    type: "book_update",
    title: "New Chapter: Answering Business Questions from Unstructured Data",
    description:
      "Added Chapter 13 covering how to transform ambiguous business questions into structured multi-agent workflows. Includes real-world analysis of YCombinator companies, cost optimization strategies, and production-ready implementation patterns.",
    date: "2025-09-21",
    requiresSignIn: true,
    isNew: false,
  },
  {
    id: "2",
    type: "feature",
    title: "Enhanced Download Experience",
    description:
      "Improved download page with better file organization and version tracking for all book formats.",
    date: "2025-09-18",
    requiresSignIn: false,
  },
];
